import { useMemo } from 'react';
import {
  activeSittingValue,
  availableFields,
  matchingPreset,
  presetsFor,
  sittingChoices,
  validateFieldModes,
} from '../../../shared/api';
import { describeSetup, fieldName as fieldNameFor } from './describeSetup';
import type { CardField, FieldMode, FlashcardConfig, Language } from '../../../shared/types';

interface Props {
  config: FlashcardConfig;
  language: Language;
  /** True when the device has no voice for this language. */
  audioUnavailable: boolean;
  voiceName: string | null;
  onChange: (next: FlashcardConfig) => void;
  sessionSize: number;
  onSessionSizeChange: (next: number) => void;
  deckSize: number;
}

const MODES: { value: FieldMode; label: string; hint: string }[] = [
  { value: 'prompt', label: 'Front', hint: 'Shown before you answer' },
  { value: 'answer', label: 'Back', hint: 'Revealed when you flip' },
  { value: 'hidden', label: 'Off', hint: 'Not shown at all' },
];

/**
 * The card setup.
 *
 * Every field is independently front, back or off, which is what makes one deck
 * serve reading, listening and production practice. Presets exist because three
 * or four tri-state toggles is a lot of thinking to do before studying, not
 * because the toggles are the advanced option — plenty of useful setups are not
 * presets.
 */
export default function FlashcardConfigPanel({
  config,
  language,
  audioUnavailable,
  voiceName,
  onChange,
  sessionSize,
  onSessionSizeChange,
  deckSize,
}: Props) {
  const fields = availableFields(language);
  const presets = presetsFor(language);
  const active = matchingPreset(config.fields, language);
  const problem = validateFieldModes(config.fields, language);

  const fieldName = useMemo(() => (field: CardField) => fieldNameFor(field, language), [language]);

  const choices = useMemo(() => sittingChoices(deckSize), [deckSize]);
  const activeSitting = activeSittingValue(sessionSize, deckSize);
  const activeChoice = choices.find((c) => c.value === activeSitting);

  const setField = (field: CardField, mode: FieldMode) =>
    onChange({ ...config, fields: { ...config.fields, [field]: mode } });

  return (
    <section className="config-panel">
      <div className="config-section">
        <h3 className="config-heading">Start from a preset</h3>
        <div className="preset-grid">
          {presets.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={`preset${active?.id === preset.id ? ' preset--active' : ''}`}
              onClick={() => onChange({ ...config, fields: { ...config.fields, ...preset.config.fields } })}
            >
              <span className="preset-name">{preset.name}</span>
              <span className="preset-description">{preset.description}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="config-section">
        <h3 className="config-heading">Or set each part yourself</h3>
        <p className="muted config-intro">
          {language.transliteration
            ? `${language.name} is written in a script and romanized as ${language.transliteration.name}, so each card has four parts you can place independently.`
            : `Each part of the card can go on the front, on the back, or be left out entirely.`}
        </p>

        <div className="field-table">
          {fields.map((field) => (
            <div key={field} className="field-row">
              <div className="field-label">
                <span className="field-name">{fieldName(field)}</span>
                {field === 'translit' && language.transliteration && (
                  <span className="field-hint">{language.transliteration.description}</span>
                )}
                {field === 'audio' && (
                  <span className="field-hint">
                    {audioUnavailable
                      ? `No ${language.name} voice on this device`
                      : voiceName
                        ? `Spoken by ${voiceName}`
                        : 'Spoken aloud'}
                  </span>
                )}
              </div>
              <div className="segmented" role="group" aria-label={fieldName(field)}>
                {MODES.map((mode) => (
                  <button
                    key={mode.value}
                    type="button"
                    title={mode.hint}
                    aria-pressed={config.fields[field] === mode.value}
                    className={`segment${config.fields[field] === mode.value ? ' segment--on' : ''}`}
                    onClick={() => setField(field, mode.value)}
                    disabled={field === 'audio' && audioUnavailable && mode.value !== 'hidden'}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <p className={problem ? 'error' : 'config-summary'}>
          {problem ?? describeSetup(config, language)}
        </p>
      </div>

      <div className="config-section">
        <h3 className="config-heading">How the session runs</h3>

        <label className="toggle-row">
          <input
            type="checkbox"
            checked={config.shuffle}
            onChange={(e) => onChange({ ...config, shuffle: e.target.checked })}
          />
          <span>
            <strong>Shuffle the deck</strong>
            <span className="field-hint">
              Off means book order, which is useful the first time through a section.
            </span>
          </span>
        </label>

        <label className="toggle-row">
          <input
            type="checkbox"
            checked={config.repeatMissed}
            onChange={(e) => onChange({ ...config, repeatMissed: e.target.checked })}
          />
          <span>
            <strong>Bring missed cards back</strong>
            <span className="field-hint">
              A card you get wrong returns later in the same session, up to twice.
            </span>
          </span>
        </label>

        <label className={`toggle-row${audioUnavailable ? ' toggle-row--disabled' : ''}`}>
          <input
            type="checkbox"
            checked={config.autoPlayAudio && !audioUnavailable}
            disabled={audioUnavailable}
            onChange={(e) => onChange({ ...config, autoPlayAudio: e.target.checked })}
          />
          <span>
            <strong>Play audio automatically</strong>
            <span className="field-hint">Speak each card as it appears, without pressing play.</span>
          </span>
        </label>

        <div className="slider-row">
          <label htmlFor="speech-rate">
            Speech speed <span className="slider-value">{config.speechRate.toFixed(2)}×</span>
          </label>
          <input
            id="speech-rate"
            type="range"
            min={0.5}
            max={1.5}
            step={0.05}
            value={config.speechRate}
            disabled={audioUnavailable}
            onChange={(e) => onChange({ ...config, speechRate: Number(e.target.value) })}
          />
          <span className="field-hint">
            Under 1× is normal for a language you are still decoding.
          </span>
        </div>

        <div className="sitting-row">
          <span className="sitting-label">Cards per sitting</span>
          <div className="segmented segmented--wrap" role="group" aria-label="Cards per sitting">
            {choices.map((choice) => (
              <button
                key={choice.value}
                type="button"
                aria-pressed={activeSitting === choice.value}
                className={`segment${activeSitting === choice.value ? ' segment--on' : ''}`}
                onClick={() => onSessionSizeChange(choice.value)}
              >
                {choice.label}
              </button>
            ))}
          </div>
          <span className="field-hint">
            {activeSitting === 0
              ? deckSize > 0
                ? `One sitting of ${deckSize}. Progress saves when you finish it.`
                : 'The whole deck in one sitting.'
              : `${activeChoice?.sittings ?? 1} sittings of ${activeSitting}. Finishing one saves your
                 progress and offers the next — the run works through the deck rather than
                 redealing the same cards.`}
          </span>
        </div>
      </div>
    </section>
  );
}
